import { Flex, Tag, Text } from '@spotify-internal/encore-web';
import {
  LabelUtils,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import { RuleFragment, getTypeOrNull } from '@spotify/confidence-graphql';
import { getRuleTypeDisplayName } from '@spotify/plugin-confidence-assignment-rules-react';
import { formatRuleId } from '@spotify/plugin-confidence-flags-react';

import { KeyValueRow } from './KeyValueRow';
import { formatAllocation, formatCriteriaCount } from './formatters';

import styles from './RuleItem.module.css';

const getRuleVariantNames = (rule: RuleFragment) =>
  (rule.assignmentSpec?.assignments ?? [])
    .map(assignment => getTypeOrNull(assignment.variant?.variant)?.name)
    .filter((name): name is string => Boolean(name))
    .map(name => extractLastNameComponent(name) ?? name);

interface RuleItemProps {
  rule: RuleFragment;
}

export const RuleItem = ({ rule }: RuleItemProps) => {
  const id = formatRuleId(rule.name);
  const ruleTypeRaw = LabelUtils.getLabelValue(rule.labels ?? [], 'ruleType');
  const ruleType =
    getRuleTypeDisplayName(ruleTypeRaw) || 'Conditional targeting';
  const variantNames = getRuleVariantNames(rule);
  const targetingKey = rule.targetingKeySelector;
  const environments = (rule.environments ?? [])
    .map(env => getTypeOrNull(env))
    .filter((env): env is NonNullable<typeof env> => Boolean(env));

  const segment = getTypeOrNull(rule.segment);
  const allocation = formatAllocation(segment?.allocation?.proportion?.value);
  const criteriaCount = segment?.targeting?.criteria?.length ?? 0;

  return (
    <Flex direction="column" className={styles.ruleItem} gap="tighter-2">
      <Flex className={styles.header} alignItems="center" gap="tighter-2">
        <Text variant="bodySmallBold" className={styles.ruleId}>
          {id}
        </Text>
        <Text variant="bodySmall" semanticColor="textSubdued">
          {ruleType}
        </Text>
        <Tag colorSet={rule.enabled !== false ? 'positiveSubdued' : undefined}>
          {rule.enabled !== false ? 'On' : 'Off'}
        </Tag>
      </Flex>
      {rule.description && <Text variant="bodySmall">{rule.description}</Text>}
      {targetingKey && (
        <KeyValueRow label="Randomized by">
          <Tag>{targetingKey}</Tag>
        </KeyValueRow>
      )}
      {variantNames.length > 0 && (
        <KeyValueRow label="Assigns">
          {variantNames.map(name => (
            <Tag key={name}>{name}</Tag>
          ))}
        </KeyValueRow>
      )}
      {allocation && (
        <KeyValueRow label="Allocation">
          <Text variant="bodySmall">{allocation}</Text>
        </KeyValueRow>
      )}
      {criteriaCount > 0 && (
        <KeyValueRow label="Target">
          <Tag>{formatCriteriaCount(criteriaCount)}</Tag>
        </KeyValueRow>
      )}
      {environments.length > 0 && (
        <KeyValueRow label="Environments">
          {environments.map(env => {
            const envLabel = extractLastNameComponent(env.name) ?? env.name;
            return <Tag key={env.name}>{envLabel}</Tag>;
          })}
        </KeyValueRow>
      )}
    </Flex>
  );
};
